// WHAT THIS CHARGE WILL MOVE — stated before it is saved, with the real numbers, and
// acknowledged before Save will work.
//
// TWO SCREENS SHOW IT, which is why it lives in components/. A price typed on a
// client-borne expense raises the charge in the same save (server/api/rest/expenses/
// charge.js), so the first charge on a topic can now convert it from the EXPENSE FORM as
// well as from the money screen's "raise charge" dialog. Both pass the same preview
// (GET /collections/conversion…) and the server refuses the conversion without the
// acknowledgement either way.
//
// Recording a charge on a job that is still a TOPIC converts it into a job that bills
// its own income, and that conversion moves money in two directions at once:
//
//   PROFIT     the retainer's profit goes UP by the job's costs — they stop rolling up
//              into it — and the job starts reporting its own revenue − its own costs.
//   THE GAP    the job becomes an income unit with a SPLIT OF ITS OWN. Its costs were
//              divided by the retainer's split until now; from here its revenue and its
//              costs are divided by the job's. Where those differ, each partner's
//              entitlement moves — and the gap is not a reporting figure.
//
// BOTH ARE SHOWN, AND THE SECOND IS NOT A FOOTNOTE. It used to be one sentence promising
// "a 50/50 split", which was also wrong whenever the partners' defaults are not 50/50:
// the conversion resolves the split from `default_share_pct`. The server now resolves it
// with the same function the write will call and returns both splits, so this prints
// what will actually happen instead of describing it.
//
// THE ACKNOWLEDGEMENT GATES SAVE, and it lives HERE rather than in the dialog. Before
// the bearer question existed a partner met this notice almost never; now every
// brokerage cost on a retainer topic arrives at it, and a warning seen every time is one
// people learn to click past. Keeping the checkbox inside the notice means the gate
// cannot be separated from the thing it is gating — moving the notice moves the gate.
//
// Renders nothing when nothing moves — a charge on a job that already bills converts no
// one, and a notice that always appears is a notice nobody reads.
//
// THE FIGURES. `movedCost` is what leaves the retainer — the job's stored costs we do not
// absorb (an absorbed cost stays on the retainer under the bearer exception, which the
// old notice ignored and so overstated the move). `otherCost` is what the job carries
// BESIDE the cost being charged, and `cost` is that cost's own amount, passed in because
// on the expense form it has not been saved yet. Job profit = amount − (otherCost + cost).
import he from '../i18n/he';
import Money, { Cost } from './Money';

const t = he.collections.board;

// Two splits are "the same" when every partner holds the same percentage in both. Order
// is not assumed: the two lists come from different tables.
const sameSplit = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  const byId = new Map(b.map((s) => [s.partnerId, s.sharePct]));
  return a.every((s) => byId.get(s.partnerId) === s.sharePct);
};

const SplitLine = ({ shares }) => (
  <span>
    {shares.map((s, i) => (
      <span key={s.partnerId}>
        {i > 0 && ' · '}
        {t.shareLine(s.partnerName, s.sharePct)}
      </span>
    ))}
  </span>
);

export default function ConversionNotice({
  preview, amount = 0, cost = 0, acknowledged, onAcknowledge,
}) {
  if (!preview || !preview.converts) return null;

  const { retainerShares, seededShares } = preview;
  const unchanged = sameSplit(retainerShares, seededShares);
  const jobCost = (Number(preview.otherCost) || 0) + (Number(cost) || 0);

  return (
    <div className="w-notice">
      <strong>{t.conversionTitle}</strong>
      <ul>
        <li>
          {t.conversionBecomesBilling(preview.jobTitle)}
        </li>
        {preview.retainerTitle && preview.retainerProfitNow != null && (
          <li>
            {t.conversionCostMoves(preview.retainerTitle)}{' '}
            <strong><Cost value={preview.movedCost} /></strong>
            {' — '}
            {t.conversionRetainerProfit}{' '}
            <Money value={preview.retainerProfitNow} /> → <strong><Money value={preview.retainerProfitAfter} /></strong>
          </li>
        )}
        <li>
          {t.conversionJobProfit}{' '}
          <Money value={amount} /> − <Cost value={jobCost} />
          {' = '}
          <strong><Money value={amount - jobCost} /></strong>
        </li>

        {/* THE SPLIT — the gap's half of the move, at the same volume as the profit's.
            Null only when no valid split can be resolved, which is also the case where
            the save is about to fail; saying so beats a dialog that simply errors. */}
        {!seededShares ? (
          <li className="w-danger">{t.conversionSplitUnavailable}</li>
        ) : (
          <li>
            {preview.retainerTitle && retainerShares && retainerShares.length > 0 && (
              <>
                {t.conversionSplitToday(preview.retainerTitle)}{' '}
                <SplitLine shares={retainerShares} />
                <br />
              </>
            )}
            {t.conversionSplitAfter}{' '}
            <strong><SplitLine shares={seededShares} /></strong>
            <div className="muted small-note">
              {unchanged ? t.conversionSplitSame : t.conversionSplitDiffers}
            </div>
          </li>
        )}
      </ul>

      {/* The gate. Unchecked, Save does not work — RecordChargeModal and the expense
          form both read this same flag, and the server refuses without it. */}
      <label className="w-ack">
        <input
          type="checkbox"
          checked={!!acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
        />
        <span>{t.conversionAck}</span>
      </label>
    </div>
  );
}
